import dayjs from 'dayjs'
import { DocumentType, DocumentV2Type } from 'types/document.type'
import { InfractionType } from 'types/infraction.type'
import { PhoneType } from 'types/phone.type'
import { Validate } from 'validate-cnj'
import {
  ALL_DIGIT_ARE_EQUALS_REGEX,
  CNJ_REGEX,
  NO_NUMBERS_OR_SPECIAL_CHARACTERS_REGEX,
  NO_NUMBERS_REGEX,
  ONLY_NUMBER_REGEX,
  REMOVE_DOTS_AND_DASHES_REGEX,
  VALID_EMAIL_REGEX,
} from './regex.util'
import { SocialNetworkType } from 'types/social-network.type'
import { extractFileExtension } from './document.util'
import { DocumentTypeId } from 'utils/enums/document-type.enum'
import { NaturalPersonAddressType, PersonAddressType } from '../types/address.type'
import { FamiliarReferenceV2Type } from 'types/familiar-reference-v2.type'
import { RefinementCtx, z } from 'zod'
import { MeasuresAppliedType } from 'types/socioeducation-measure-measures-applied.type'

export const NOT_IN_TYPES_DOCUMENT: string[] = [
  DocumentTypeId.CERTIDAO_DE_NASCIMENTO,
  DocumentTypeId.CERTIDAO_DE_OBITO,
]

export const doNotHasText = (text?: string | number | boolean | null) => {
  if (typeof text === 'string' && text.trim() !== '') {
    return text === '0'
  }

  if (typeof text === 'number' && !isNaN(text)) {
    return false
  }

  if (typeof text === 'boolean') {
    return false
  }

  return true
}

export const doNotNullOrEmpty = (value: string | null | undefined) => {
  return value !== null || value !== '' || value !== undefined
}

export const validateField = (field?: any): string => {
  return field && String(field) !== 'null' ? String(field) : ''
}

export const validateFieldNumber = (value?: any): string => {
  const validatedValue = validateField(value)
  return validatedValue === '0' ? '' : validatedValue
}

export const isEmail = (value?: any): boolean => {
  const validatedValue = validateField(value)
  if (validatedValue.length > 0) {
    const isValid = VALID_EMAIL_REGEX.test(validatedValue)
    if (!isValid) {
      return false
    }
  }
  return true
}

export const isCpf = (value?: any): boolean => {
  const validatedValue = validateField(value)
  if (validatedValue.length > 0) {
    const cleanedCPF = validatedValue.replace(ONLY_NUMBER_REGEX, '')

    if (cleanedCPF.length !== 11) {
      return false
    }

    if (ALL_DIGIT_ARE_EQUALS_REGEX.test(cleanedCPF)) {
      return false
    }

    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanedCPF.charAt(i), 10) * (10 - i)
    }
    const remainder = sum % 11
    const digit1 = remainder < 2 ? 0 : 11 - remainder

    sum = 0
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanedCPF.charAt(i), 10) * (11 - i)
    }
    const remainder2 = sum % 11
    const digit2 = remainder2 < 2 ? 0 : 11 - remainder2

    return (
      parseInt(cleanedCPF.charAt(9), 10) === digit1 &&
      parseInt(cleanedCPF.charAt(10), 10) === digit2
    )
  }
  return true
}

export const hasNumbers = (value?: string | null): boolean => {
  if (!value) {
    return false
  }
  return NO_NUMBERS_REGEX.test(value)
}

export const isValidName = (value?: any): boolean => {
  const validatedValue = validateField(value)
  if (validatedValue.length >= 0) {
    const isValid = NO_NUMBERS_OR_SPECIAL_CHARACTERS_REGEX.test(validatedValue)
    if (!isValid) {
      return false
    }
  }

  return true
}

export const validateFieldDate = (field?: any): string => {
  const isValidDate = isDate(field)
  if (isValidDate) {
    return field
  }
  return ''
}

export const isDate = (date?: string | null): boolean => {
  if (!date) {
    return false
  }

  if (dayjs(date, 'DD/MM/YYYY').isValid()) {
    const parse = dayjs(date, 'DD/MM/YYYY').toDate()
    return !isNaN(parse.getTime())
  }

  const parsedDate = dayjs(date, 'YYYY-MM-DD').toDate()
  return !isNaN(parsedDate.getTime())
}

export const isFutureDate = (date?: string | null): boolean => {
  const now = new Date()
  const parsedDate = dayjs(date, 'YYYY-MM-DD').toDate()
  return parsedDate.getTime() < now.getTime()
}

export const validateDocuments = (items?: any[]): DocumentType[] | null => {
  if (items && items.length > 0 && items.length) {
    const documents = items as DocumentType[]
    const validatedItems: DocumentType[] = documents
      .filter((item) => validateDocument(item))
      .map((item) => item)
    return validatedItems.length > 0 ? validatedItems : null
  }
  return null
}

export function isFilled(value: any): boolean {
  if (Array.isArray(value)) {
    return value.length > 0 && value.every(isFilled) // Verifica cada item no array
  } else if (typeof value === 'object' && value !== null) {
    // Verifica recursivamente cada valor de todas as propriedades do objeto
    return Object.values(value).some((val) => isFilled(val)) // Verifica se algum valor é preenchido
  } else if (typeof value === 'string') {
    return value.trim() !== '' && value.trim() !== '0' // Verifica se a string não está vazia
  } else if (typeof value === 'number') {
    return !isNaN(value) // Verifica se é um número válido (não NaN)
  } else {
    // Verifica se é valor simples (não é null, undefined, string vazia ou 0)
    return value !== null && value !== undefined && value !== ''
  }
}

// Função auxiliar para adicionar issue ao contexto, agora tipada
export function addIssueIfInvalid(
  ctx: RefinementCtx, // Tipo do contexto de superRefine
  condition: boolean, // Condição que, se verdadeira, adiciona a issue
  index: number, // Índice no array
  fieldPath: (string | number)[], // Caminho do campo no objeto
  message: string, // Mensagem de erro
): void {
  if (condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [index, ...fieldPath], // Caminho onde o erro será reportado
    })
  }
}

export const validateFamiliarReferencesV2 = (items?: any[]): FamiliarReferenceV2Type[] => {
  if (items && items.length > 0 && items.length) {
    const references = items as FamiliarReferenceV2Type[]
    const validatedItems: FamiliarReferenceV2Type[] = references
      .filter(
        (item) =>
          (!doNotHasText(item.tipoReferencia.id) || item.tipoReferencia.id) &&
          item.pessoaReferencia &&
          !doNotHasText(item.pessoaReferencia.nome),
      )
      .map((item) => item)
    return validatedItems.length > 0 ? validatedItems : []
  }
  return []
}

export const validateDocumentsV2 = (items?: any[]): DocumentV2Type[] | null => {
  if (items && items.length > 0 && items.length) {
    const documents = items as DocumentV2Type[]
    const validatedItems: DocumentV2Type[] = documents
      .filter((item) => validateDocumentV2(item))
      .map((item) => item)
    return validatedItems.length > 0 ? validatedItems : []
  }
  return []
}

export const validateDocument = (documento: DocumentType) => {
  return (
    (NOT_IN_TYPES_DOCUMENT.includes(documento.idTipoDocumento) && documento.matricula !== '') ||
    (!NOT_IN_TYPES_DOCUMENT.includes(documento.idTipoDocumento) && documento.numeroDocumento !== '')
  )
}

export const validateDocumentV2 = (documento: DocumentV2Type) => {
  const tipoDocumentoId = String(documento.tipoDocumento.id)
  return (
    (NOT_IN_TYPES_DOCUMENT.includes(tipoDocumentoId) && documento.matricula !== '') ||
    (!NOT_IN_TYPES_DOCUMENT.includes(tipoDocumentoId) && documento.numeroDocumento !== '')
  )
}

export const validatePhones = (items?: any[]): PhoneType[] | null => {
  if (items && items.length > 0) {
    const phones = items as PhoneType[]
    const validatedItems: PhoneType[] = phones
      .filter((item) => item.telefone !== '')
      .map((item) => ({
        id: item.id,
        telefone: validateField(item.telefone),
        descricao: validateField(item.descricao),
      }))
    return validatedItems.length > 0 ? validatedItems : []
  }
  return []
}

export const validateSocialNetworks = (items?: any[]): SocialNetworkType[] | null => {
  if (items && items.length > 0) {
    const socialNetworks = items as SocialNetworkType[]
    const validatedItems: SocialNetworkType[] = socialNetworks
      .filter((item) => item.redeSocial !== '')
      .map((item) => ({
        id: item.id,
        redeSocial: validateField(item.redeSocial),
        descricao: validateField(item.descricao),
      }))
    return validatedItems.length > 0 ? validatedItems : null
  }
  return null
}

export const validateAddresses = (items?: any[]): PersonAddressType[] | null => {
  if (items && items.length > 0 && items.length) {
    const addresses = items as PersonAddressType[]
    const validatedItems: PersonAddressType[] = addresses
      .filter((item) => item.endereco.cep !== '')
      .map((item) => item)
    return validatedItems.length > 0 ? validatedItems : null
  }

  return null
}

export const validateAddressesV2 = (items?: any[]): NaturalPersonAddressType[] => {
  if (items && items.length > 0 && items.length) {
    const addresses = items as NaturalPersonAddressType[]
    const validatedItems: NaturalPersonAddressType[] = addresses
      .filter((item) => !doNotHasText(item.endereco.cep))
      .map((item) => item)
    return validatedItems.length > 0 ? validatedItems : []
  }

  return []
}

export const validateMeasuresApplied = (items?: any[]): MeasuresAppliedType[] => {
  if (items && items.length > 0 && items.length) {
    const measuresApplied = items as MeasuresAppliedType[]
    const validatedItems: MeasuresAppliedType[] = measuresApplied.map((item) => item)
    return validatedItems.length > 0 ? validatedItems : []
  }

  return []
}

export const validateInfractions = (items?: any): InfractionType[] | null => {
  if (items && items.length > 0 && items.length) {
    const infractions = items as InfractionType[]
    const validatedItems: InfractionType[] = infractions.map((item) => item)

    return validatedItems.length > 0 ? validatedItems : null
  }
  return null
}

export const isCnj = (value?: any): boolean => {
  try {
    if (!CNJ_REGEX.test(value) || value.length < 14) {
      return false
    }
    const processNumber = value.replace(REMOVE_DOTS_AND_DASHES_REGEX, '')
    const loadedNumber = Validate.load(value).generate(false)
    return processNumber === loadedNumber
  } catch (e) {
    return false
  }
}

export const isImage = (file: File): boolean => {
  const acceptedFormats = ['jpg', 'jpeg', 'png']
  const maxFileSize = 2 * 1024 * 1024 // 2MB

  const fileExtension = extractFileExtension(file.name)
  if (!fileExtension || !acceptedFormats.includes(fileExtension.toLocaleLowerCase())) {
    return false
  }

  return file.size <= maxFileSize
}

export const isDocumentFile = (file: File): boolean => {
  const acceptedFormats = ['jpg', 'jpeg', 'png', 'pdf']
  const maxFileSizeInBytes = 10 * 1024 * 1024 // 10MB

  const fileExtension = extractFileExtension(file.name)
  if (!fileExtension || !acceptedFormats.includes(fileExtension.toLocaleLowerCase())) {
    return false
  }

  return file.size <= maxFileSizeInBytes
}

export function parseBoolean(value?: string | null | boolean): boolean | null {
  if (typeof value === 'boolean') {
    return value
  } else if (value === 'true') {
    return true
  } else if (value === 'false') {
    return false
  }
  return null // Retorna `null` caso não seja 'true' nem 'false'.
}
